// FutureFridges Sentinel (GIOTHUB-163): config.json -> sign-in -> the
// Sentinel frame (the static demo's look) around the pages. The shell gives
// sign-in, session and permissions, the API client, languages and routing.
import "@fontsource/ibm-plex-sans/latin-400.css";
import "@fontsource/ibm-plex-sans/latin-500.css";
import "@fontsource/ibm-plex-sans/latin-600.css";
import "@fontsource/ibm-plex-sans/latin-700.css";
import "@fontsource/ibm-plex-mono/latin-400.css";
import "@fontsource/ibm-plex-mono/latin-500.css";
import "@fontsource/ibm-plex-sans-condensed/latin-600.css";
import "@fontsource/ibm-plex-sans-condensed/latin-700.css";
import "./styles/shell.css";
import "./styles/app.css";
import { AppRoutes, Frame, registerFrame, registerRoutes, startApp, type Label } from "@gimasi/giothub-shell";
import { Gate } from "./frame/Gate";
import { SentinelFrame } from "./frame/SentinelFrame";
import { translate } from "./i18n";
import { installAccountHeader } from "./lib/account";
import Alarms from "./pages/Alarms";
import Ask from "./pages/Ask";
import Cabinet from "./pages/Cabinet";
import ControlRoom from "./pages/ControlRoom";
import Customers from "./pages/Customers";
import Dashboards from "./pages/Dashboards";
import Escalation from "./pages/Escalation";
import Insights from "./pages/Insights";
import Groups from "./pages/Groups";
import Location from "./pages/Location";
import Mobile from "./pages/Mobile";
import DeviceDetail from "./pages/DeviceDetail";
import { Cabinets, Devices } from "./pages/Lists";
import Network from "./pages/Network";
import Overview from "./pages/Overview";
import Partner from "./pages/Partner";
import Quality from "./pages/Quality";
import Rules from "./pages/Rules";
import Settings from "./pages/Settings";
import Users from "./pages/Users";

/** a menu label in all four languages from Sentinel's dictionaries */
const L = (key: string): Label => ({ en: translate("en", key), de: translate("de", key), it: translate("it", key), fr: translate("fr", key) });

installAccountHeader();
// Sentinel always shows its own frame: also under the default id "sidebar",
// so config.json needs no "frame" field (the console sends none).
registerFrame("sentinel", SentinelFrame);
registerFrame("sidebar", SentinelFrame);
registerRoutes([
  { path: "/", component: Overview, perm: "devices:read", menu: { label: L("nav.overview"), icon: "overview", group: L("nav.monitor") } },
  { path: "/loc/*", component: Location, perm: "devices:read" },
  { path: "/loc", component: Location, perm: "devices:read", menu: { label: L("nav.locations"), icon: "locations", group: L("nav.monitor") } },
  { path: "/cabinets/:id", component: Cabinet, perm: "devices:read" },
  { path: "/cabinets", component: Cabinets, perm: "devices:read", menu: { label: L("nav.cabinets"), icon: "cabinet", group: L("nav.monitor") } },
  { path: "/alarms/:id", component: Alarms, perm: "alarms:read" },
  { path: "/alarms", component: Alarms, perm: "alarms:read", menu: { label: L("nav.alarms"), icon: "bell", group: L("nav.monitor") } },
  { path: "/control", component: ControlRoom, perm: "devices:read", menu: { label: L("nav.control"), icon: "dash", group: L("nav.monitor") } },
  { path: "/mobile", component: Mobile, perm: "devices:read", menu: { label: L("nav.mobile"), icon: "sensor", group: L("nav.monitor") } },
  { path: "/devices", component: Devices, perm: "devices:read", menu: { label: L("nav.devices"), icon: "sensor", group: L("nav.setup") } },
  { path: "/devices/:device_id", component: DeviceDetail, perm: "devices:read" },
  { path: "/network", component: Network, perm: "devices:read", menu: { label: L("nav.network"), icon: "path", group: L("nav.setup") } },
  { path: "/escalation", component: Escalation, perm: "alarms:read", menu: { label: L("nav.escalation"), icon: "users", group: L("nav.setup") } },
  { path: "/groups/:tab", component: Groups, perm: "devices:read" },
  { path: "/groups", component: Groups, perm: "devices:read", menu: { label: L("nav.groups"), icon: "tree", group: L("nav.setup") } },
  { path: "/rules/:tab", component: Rules, perm: "devices:read" },
  { path: "/rules", component: Rules, perm: "devices:read", menu: { label: L("nav.rules"), icon: "rules", group: L("nav.setup") } },
  { path: "/ask", component: Ask, perm: "interactions:use", ai: true, menu: { label: L("nav.ask"), icon: "ai", group: L("nav.insight") } },
  { path: "/insights", component: Insights, perm: "devices:read", menu: { label: L("nav.insights"), icon: "layers", group: L("nav.insight") } },
  { path: "/dashboards/:id", component: Dashboards, perm: "devices:read" },
  { path: "/dashboards", component: Dashboards, perm: "devices:read", menu: { label: L("nav.dashboards"), icon: "dash", group: L("nav.insight") } },
  { path: "/quality/:tab", component: Quality, perm: "devices:read" },
  { path: "/quality", component: Quality, perm: "devices:read", menu: { label: L("nav.quality"), icon: "report", group: L("nav.insight") } },
  { path: "/customers", component: Customers, perm: "accounts:manage", menu: { label: L("nav.customers"), icon: "partner", group: L("nav.partner") } },
  { path: "/partner/:tab", component: Partner, perm: "devices:transfer" },
  { path: "/partner", component: Partner, perm: "devices:transfer", menu: { label: L("nav.partnerConsole"), icon: "layers", group: L("nav.partner") } },
  { path: "/users", component: Users, perm: "users:manage", menu: { label: L("nav.users"), icon: "users", group: L("nav.admin") } },
  { path: "/settings", component: Settings, perm: "devices:read", menu: { label: L("nav.settings"), icon: "rules", group: L("nav.admin") } },
]);

startApp(() => (
  <Gate>
    <Frame><AppRoutes /></Frame>
  </Gate>
));
